// Ett enda ställe som läser och skriver lokala utkast.
// Regeln: servern är sanningen. Lokalt sparat är bara ett skyddsnät
// som användaren själv får välja att återställa.
import { draftKey, isManagedKey, type DraftScope } from "./keys";

export interface DraftEnvelope<T = unknown> {
  /** Innehållet användaren höll på med. */
  data: T;
  /** När utkastet skrevs lokalt. */
  savedAt: string;
  /** Serverns updated_at vid tidpunkten då utkastet skrevs, om känd. */
  baseUpdatedAt?: string | null;
  /** Sant när utkastet aldrig nått servern. */
  unsynced: boolean;
  scope: DraftScope;
}

const available = (): boolean => {
  try {
    return typeof window !== "undefined" && !!window.localStorage;
  } catch {
    return false;
  }
};

export const saveDraft = <T>(
  scope: DraftScope,
  data: T,
  options: { baseUpdatedAt?: string | null; unsynced?: boolean } = {}
): boolean => {
  if (!available()) return false;
  const envelope: DraftEnvelope<T> = {
    data,
    savedAt: new Date().toISOString(),
    baseUpdatedAt: options.baseUpdatedAt ?? null,
    unsynced: options.unsynced ?? true,
    scope,
  };
  try {
    localStorage.setItem(draftKey(scope), JSON.stringify(envelope));
    return true;
  } catch {
    // Fullt utrymme: rensa äldsta utkast och försök en gång till.
    pruneOldest(5);
    try {
      localStorage.setItem(draftKey(scope), JSON.stringify(envelope));
      return true;
    } catch {
      return false;
    }
  }
};

export const readDraft = <T>(scope: DraftScope): DraftEnvelope<T> | null => {
  if (!available()) return null;
  try {
    const raw = localStorage.getItem(draftKey(scope));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as DraftEnvelope<T>;
    return parsed && typeof parsed === "object" && "data" in parsed ? parsed : null;
  } catch {
    return null;
  }
};

export const clearDraft = (scope: DraftScope): void => {
  if (!available()) return;
  try {
    localStorage.removeItem(draftKey(scope));
  } catch {
    /* tyst */
  }
};

/** Alla utkast, nyast först. Används av återställningsvyer. */
export const listDrafts = (filter?: Partial<DraftScope>): DraftEnvelope[] => {
  if (!available()) return [];
  const out: DraftEnvelope[] = [];
  for (let i = 0; i < localStorage.length; i += 1) {
    const key = localStorage.key(i);
    if (!key || !isManagedKey(key)) continue;
    try {
      const parsed = JSON.parse(localStorage.getItem(key) || "") as DraftEnvelope;
      if (!parsed?.scope) continue;
      if (filter?.kind && parsed.scope.kind !== filter.kind) continue;
      if (filter?.projectId && parsed.scope.projectId !== filter.projectId) continue;
      if (filter?.folderId && parsed.scope.folderId !== filter.folderId) continue;
      out.push(parsed);
    } catch {
      /* hoppa över trasiga poster */
    }
  }
  return out.sort((a, b) => (a.savedAt < b.savedAt ? 1 : -1));
};

/** Rensar de äldsta utkasten när utrymmet tar slut. */
export const pruneOldest = (count: number): void => {
  const drafts = listDrafts();
  drafts
    .slice(-count)
    .forEach((draft) => clearDraft(draft.scope));
};

/** Rensar allt lokalt utkastsdata – används vid utloggning. */
export const clearAllDrafts = (): void => {
  if (!available()) return;
  const keys: string[] = [];
  for (let i = 0; i < localStorage.length; i += 1) {
    const key = localStorage.key(i);
    if (key && isManagedKey(key)) keys.push(key);
  }
  keys.forEach((key) => localStorage.removeItem(key));
};
